import { KTag } from '@/ui-library/components';
import { interviewStatusMap } from '@/constants/interview-constants';
import type { InterviewType } from '@/types/interview';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { getInterviewTimeDetails } from '@/utils/interview-utils';
import { Circle, Box } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

const InterviewTimer = ({
  startTime,
  endTime,
}: {
  startTime: string;
  endTime: string;
}) => {
  const [elapsedTime, setElapsedTime] = useState('00:00');
  const [isRunning, setIsRunning] = useState(true);

  useEffect(() => {
    const updateTimer = () => {
      const start = dayjs(startTime);
      const end = dayjs(endTime);
      const now = dayjs();

      // Check if interview has ended
      if (now.isAfter(end)) {
        setIsRunning(false);
        // Calculate final duration
        const finalElapsed = end.diff(start, 'minute');
        const hours = Math.floor(finalElapsed / 60);
        const minutes = finalElapsed % 60;
        const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}`;
        setElapsedTime(formattedTime);
        return;
      }

      // Calculate current elapsed time
      const elapsed = now.diff(start, 'minute');
      if (elapsed > 0) {
        const hours = Math.floor(elapsed / 60);
        const minutes = elapsed % 60;
        const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}`;
        setElapsedTime(formattedTime);
      }
    };

    updateTimer();

    // Only set interval if timer is still running
    let interval: NodeJS.Timeout | null = null;
    if (isRunning) {
      interval = setInterval(updateTimer, 60000); // Update every minute
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [startTime, endTime, isRunning]);

  return <>{elapsedTime}</>;
};

const StatusWithTimer = ({
  label,
  showTimer,
  startTime,
  endTime,
}: {
  label: string;
  showTimer: boolean;
  startTime: string;
  endTime: string;
}) => {
  if (showTimer) {
    return (
      <>
        <Circle
          display="inline-flex"
          mr="4px"
          mb="1px"
          size="8px"
          bg="status-4"
        ></Circle>
        {label} <InterviewTimer startTime={startTime} endTime={endTime} />
      </>
    );
  }
  return <>{label}</>;
};

export const InterviewStatusTag = ({
  interview,
  interviewId,
}: {
  interview: InterviewType;
  interviewId?: number;
}) => {
  const history = useHistory();
  const { meeting_status: status } = interview;
  const { isInterviewInProgress, isAfterInterview } =
    getInterviewTimeDetails(interview);

  // Determine the status to display
  const getDisplayStatus = () => {
    if (
      isInterviewInProgress &&
      status !== 'candidate_no_show' &&
      status !== 'cancelled'
    ) {
      return {
        label: 'Interview in-progress',
        bg: 'pastel-2-light',
        color: 'pastel-2-dark',
        showTimer: true,
      };
    }

    if (
      isAfterInterview &&
      status !== 'candidate_no_show' &&
      status !== 'cancelled' &&
      (status === 'not_started' || status === 'in_progress')
    ) {
      const endTime = dayjs(interview.end_time);
      const now = dayjs();
      const hoursSinceEnd = now.diff(endTime, 'hour');

      if (hoursSinceEnd <= 1) {
        return {
          label: 'Interview completed',
          bg: 'pastel-2-light',
          color: 'pastel-2-dark',
        };
      }
    }

    return {
      ...(interviewStatusMap[status] || {}),
      showTimer: false,
    };
  };

  const displayStatus = getDisplayStatus();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (interviewId) {
      history.push(`/interviews/${interviewId}`);
    }
  };

  return (
    <Box
      display="inline-block"
      cursor={interviewId ? 'pointer' : 'default'}
      onClick={interviewId ? handleClick : undefined}
    >
      <KTag
        value={
          <StatusWithTimer
            label={displayStatus.label}
            showTimer={displayStatus.showTimer}
            startTime={interview.start_time}
            endTime={interview.end_time}
          />
        }
        color={displayStatus.color}
        bg={displayStatus.bg}
        whiteSpace="nowrap"
      />
    </Box>
  );
};
